// @flow

import React, { type Node, useCallback } from 'react';
import styled, { css } from 'styled-components';

import { useSubmitting } from '@tectonics/prs/submitting';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { useUniqueId } from '@tectonics/hooks';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';

import { RadioButton } from '../../../../../atoms';
import { PaymentMethodName } from '../../payment-method-name';
import { DisplayBrands } from './display-brands';
import { DisplayInstructions } from './display-instructions';

type Props = {|
  paymentMethodCode: string,
  handleFocus: () => void,
|};

export function PaymentMethodItem({ paymentMethodCode, handleFocus }: Props): Node {
  const [
    selectedPaymentMethod,
    setSelectEbanxPaymentMethod,
  ] = useUserInputValue('selectedPaymentMethod');

  const handleSelectPaymentMethod = useCallback((event) => {
    setSelectEbanxPaymentMethod(event.target.value);
  }, [setSelectEbanxPaymentMethod]);

  const isChecked = paymentMethodCode === selectedPaymentMethod;
  const submitting = useSubmitting();
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  const inputId = useUniqueId();

  return (
    <>
      <Label htmlFor={inputId} selected={isChecked} data-test-value={paymentMethodCode}>
        <RadioButton
          id={inputId}
          checked={isChecked}
          value={paymentMethodCode}
          onChange={handleSelectPaymentMethod}
          disabled={submitting}
          onFocus={handleFocus}
          onBlur={handleFocus}
          name="payment-method-item"
        />

        <PaymentMethodNameWrapper>
          <PaymentMethodName
            paymentMethod={paymentMethodCode}
            country={country}
          />
        </PaymentMethodNameWrapper>

        <DisplayBrandsWrapper>
          <DisplayBrands paymentMethodCode={paymentMethodCode} />
        </DisplayBrandsWrapper>
      </Label>

      <DisplayInstructionsWrapper selected={isChecked}  >
        <DisplayInstructions
          paymentMethodCode={paymentMethodCode}
        />
      </DisplayInstructionsWrapper>
    </>
  );
}

const Label = styled.label`
  position: relative;
  display: flex;
  cursor: pointer;
  padding: 16px;
  align-items: center;

  &:first-child {
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
  }

  &:last-child {
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  ${props => props.selected && css`
    background: ${props => props.theme.colors.gray(96)};
  `}
`;

const PaymentMethodNameWrapper = styled.span`
  font-size: 0.9em;
  font-weight: 600;
  flex-grow: 1;
  color: ${props => props.theme.colors.secondary(30)};

  [dir='ltr'] & {
    margin-left: 12px;
  }

  [dir='rtl'] & {
    margin-right: 12px;
  }
`;

const DisplayBrandsWrapper = styled.div`
  height: 20px;
`;

const DisplayInstructionsWrapper = styled.div`

  transition: max-height 400ms ease-in-out;
  overflow: hidden;
  max-height: 1000px;
  ${props => !props.selected && css`
    max-height: 0;
  `}
  ${props => props.selected && css`
    overflow: visible;
  `}
`;
